import { useState } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { AlertTriangle } from "lucide-react";

interface DeleteDeviceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  deviceName: string;
}

export function DeleteDeviceDialog({
  isOpen,
  onClose,
  onConfirm,
  deviceName,
}: DeleteDeviceDialogProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      console.error("Failed to delete device:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Device">
      <div className="space-y-4">
        <div className="flex items-center gap-3 p-4 bg-red-50 text-red-700 rounded-lg border border-red-100">
          <AlertTriangle size={24} className="shrink-0" />
          <p className="text-sm font-medium">
            Are you sure you want to delete this device? This will force the
            user to log in again.
          </p>
        </div>

        <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
          <p className="text-sm text-gray-500 mb-1">Device:</p>
          <p className="font-semibold text-gray-900">{deviceName}</p>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={isLoading}
            className="bg-red-600 hover:bg-red-700 border-red-600 hover:border-red-700 text-white"
          >
            {isLoading ? "Deleting..." : "Delete Device"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
